import type { AppKyc } from "../types";
import { AppKeyStatus } from "../types";

export interface AppKycGatewayTypeCount {
  gatewayType: string;
  count: number;
}

export interface AppKycCompanyCount {
  companyName: string;
  count: number;
}

export interface AppKycAnalytics {
  total: number;
  active: number;
  inactive: number;
  disabled: number;
  blocked: number;
  expiringWithin30Days: number;
  expired: number;
  noExpiryDate: number;
  withContactEmail: number;
  byGatewayType: AppKycGatewayTypeCount[];
  byCompany: AppKycCompanyCount[];
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function computeAppKycAnalytics(items: AppKyc[]): AppKycAnalytics {
  const now = Date.now();
  const in30Days = now + 30 * MS_PER_DAY;

  const statusCounts: Record<AppKeyStatus, number> = {
    [AppKeyStatus.ACTIVE]: 0,
    [AppKeyStatus.INACTIVE]: 0,
    [AppKeyStatus.DISABLED]: 0,
    [AppKeyStatus.BLOCKED]: 0,
  };

  const gatewayTypeMap = new Map<string, number>();
  const companyMap = new Map<string, number>();

  let expiringWithin30Days = 0;
  let expired = 0;
  let noExpiryDate = 0;
  let withContactEmail = 0;

  for (const item of items) {
    statusCounts[item.appKeyStatus] = (statusCounts[item.appKeyStatus] ?? 0) + 1;

    const gatewayType = item.providerGatewayType?.trim() || "Unknown";
    gatewayTypeMap.set(gatewayType, (gatewayTypeMap.get(gatewayType) ?? 0) + 1);

    const company = item.contactCompanyName?.trim();
    if (company) {
      companyMap.set(company, (companyMap.get(company) ?? 0) + 1);
    }

    if (item.contactEmail?.trim()) {
      withContactEmail += 1;
    }

    if (!item.keyExpiresOn) {
      noExpiryDate += 1;
    } else {
      const expires = new Date(item.keyExpiresOn).getTime();
      if (!Number.isNaN(expires)) {
        if (expires < now) {
          expired += 1;
        } else if (expires <= in30Days) {
          expiringWithin30Days += 1;
        }
      }
    }
  }

  const byGatewayType = [...gatewayTypeMap.entries()]
    .map(([gatewayType, count]) => ({ gatewayType, count }))
    .sort((a, b) => b.count - a.count || a.gatewayType.localeCompare(b.gatewayType));

  const byCompany = [...companyMap.entries()]
    .map(([companyName, count]) => ({ companyName, count }))
    .sort((a, b) => b.count - a.count || a.companyName.localeCompare(b.companyName))
    .slice(0, 10);

  return {
    total: items.length,
    active: statusCounts[AppKeyStatus.ACTIVE],
    inactive: statusCounts[AppKeyStatus.INACTIVE],
    disabled: statusCounts[AppKeyStatus.DISABLED],
    blocked: statusCounts[AppKeyStatus.BLOCKED],
    expiringWithin30Days,
    expired,
    noExpiryDate,
    withContactEmail,
    byGatewayType,
    byCompany,
  };
}
